import React, { useState, useEffect } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const EmissionsResults = ({ voyageData, fuelData, routeData }) => {
  const [calculationResults, setCalculationResults] = useState(null);
  const [isCalculating, setIsCalculating] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');

  const calculateEmissions = () => {
    setIsCalculating(true);
    
    // Simulate calculation delay
    setTimeout(() => {
      const mockResults = {
        totalEmissions: 1247.8,
        etsEmissions: 892.3,
        nonEtsEmissions: 355.5,
        etsCost: 67420.0,
        fueleuCompliance: {
          ghgIntensity: 91.2,
          targetIntensity: 89.5,
          complianceStatus: 'non-compliant',
          penaltyRisk: 15600.0
        },
        ciiRating: {
          current: 'C',
          projected: 'D',
          riskLevel: 'high',
          impactOnFleet: 'negative'
        },
        segmentBreakdown: [
          {
            segment: 'Rotterdam to Gibraltar',
            emissions: 445.2,
            etsApplicable: true,
            cost: 26712.0
          },
          {
            segment: 'Gibraltar to Suez',
            emissions: 523.1,
            etsApplicable: false,
            cost: 0
          },
          {
            segment: 'Suez to Singapore',
            emissions: 279.5,
            etsApplicable: false,
            cost: 0
          }
        ],
        fuelBreakdown: [
          { fuel: 'VLSFO', consumption: 245.8, emissions: 774.7, percentage: 62.1 },
          { fuel: 'MGO', consumption: 89.2, emissions: 286.1, percentage: 22.9 },
          { fuel: 'Boiler', consumption: 58.4, emissions: 187.0, percentage: 15.0 }
        ],
        comparisonData: {
          fleetAverage: 1156.4,
          industryBenchmark: 1089.2,
          bestInClass: 945.7,
          performance: 'below-average'
        },
        costAllocation: {
          ownerShare: 40320.0,
          chartererShare: 27100.0,
          bimcoClause: 'ETS-2024',
          allocationMethod: '60/40 split'
        }
      };
      
      setCalculationResults(mockResults);
      setIsCalculating(false);
    }, 2000);
  };

  const exportResults = () => {
    console.log('Exporting calculation results...');
  };

  const saveCalculation = () => {
    console.log('Saving calculation...');
  };

  const getComplianceColor = (status) => {
    switch (status) {
      case 'compliant': return 'text-success';
      case 'non-compliant': return 'text-error';
      case 'at-risk': return 'text-warning';
      default: return 'text-muted-foreground';
    }
  };

  const getCIIRatingColor = (rating) => {
    switch (rating) {
      case 'A': return 'text-success';
      case 'B': return 'text-success';
      case 'C': return 'text-warning';
      case 'D': return 'text-error';
      case 'E': return 'text-error';
      default: return 'text-muted-foreground';
    }
  };

  const COLORS = ['#3182CE', '#38A169', '#D69E2E', '#E53E3E'];

  const tabs = [
    { id: 'overview', label: 'Overview', icon: 'BarChart3' },
    { id: 'breakdown', label: 'Breakdown', icon: 'PieChart' },
    { id: 'compliance', label: 'Compliance', icon: 'Shield' },
    { id: 'comparison', label: 'Comparison', icon: 'TrendingUp' }
  ];

  if (!calculationResults) {
    return (
      <div className="maritime-card p-6">
        <div className="text-center py-12">
          <Icon name="Calculator" size={48} className="text-muted-foreground mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-foreground mb-2">Ready to Calculate</h2>
          <p className="text-muted-foreground mb-6">
            Complete the voyage details and fuel consumption data to calculate emissions
          </p>
          <Button 
            onClick={calculateEmissions}
            loading={isCalculating}
            iconName="Play"
            iconPosition="left"
            className="px-8"
          >
            {isCalculating ? 'Calculating...' : 'Calculate Emissions'}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="maritime-card p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-semibold text-foreground">Emissions Calculation Results</h2>
          <p className="text-sm text-muted-foreground">
            Voyage: {voyageData?.voyageNumber || 'AV-2024-089'} • Calculated on {new Date()?.toLocaleDateString()}
          </p>
        </div>
        <div className="flex items-center space-x-2">
          <Button variant="outline" onClick={saveCalculation} iconName="Save" iconPosition="left">
            Save
          </Button>
          <Button variant="outline" onClick={exportResults} iconName="Download" iconPosition="left">
            Export
          </Button>
          <Button onClick={calculateEmissions} iconName="RefreshCw" iconPosition="left">
            Recalculate
          </Button>
        </div>
      </div>
      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="maritime-card p-4">
          <div className="text-2xl font-bold text-foreground">{calculationResults?.totalEmissions?.toLocaleString()}</div>
          <div className="text-sm text-muted-foreground">Total CO₂e (tonnes)</div>
        </div>
        <div className="maritime-card p-4">
          <div className="text-2xl font-bold text-warning">€{calculationResults?.etsCost?.toLocaleString()}</div>
          <div className="text-sm text-muted-foreground">ETS Cost</div>
        </div>
        <div className="maritime-card p-4">
          <div className={`text-2xl font-bold ${getCIIRatingColor(calculationResults?.ciiRating?.projected)}`}>
            {calculationResults?.ciiRating?.projected}
          </div>
          <div className="text-sm text-muted-foreground">Projected CII Rating</div>
        </div>
        <div className="maritime-card p-4">
          <div className={`text-2xl font-bold ${getComplianceColor(calculationResults?.fueleuCompliance?.complianceStatus)}`}>
            {calculationResults?.fueleuCompliance?.ghgIntensity}
          </div>
          <div className="text-sm text-muted-foreground">GHG Intensity (gCO₂e/MJ)</div>
        </div>
      </div>
      {/* Tabs */}
      <div className="border-b border-border mb-6">
        <nav className="flex space-x-8">
          {tabs?.map((tab) => (
            <button
              key={tab?.id}
              onClick={() => setActiveTab(tab?.id)}
              className={`flex items-center space-x-2 py-2 px-1 border-b-2 font-medium text-sm transition-maritime ${
                activeTab === tab?.id
                  ? 'border-primary text-primary' :'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              <Icon name={tab?.icon} size={16} />
              <span>{tab?.label}</span>
            </button>
          ))}
        </nav>
      </div>
      {/* Tab Content */}
      {activeTab === 'overview' && (
        <div className="space-y-6">
          {/* Emissions by Segment */}
          <div>
            <h3 className="text-lg font-medium text-foreground mb-4">Emissions by Route Segment</h3>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={calculationResults?.segmentBreakdown}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="segment" />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="emissions" fill="#3182CE" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Cost Allocation */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="maritime-card p-4">
              <h4 className="text-lg font-medium text-foreground mb-4">Cost Allocation</h4>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Owner Share (60%)</span>
                  <span className="font-medium text-foreground">€{calculationResults?.costAllocation?.ownerShare?.toLocaleString()}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Charterer Share (40%)</span>
                  <span className="font-medium text-foreground">€{calculationResults?.costAllocation?.chartererShare?.toLocaleString()}</span>
                </div>
                <div className="pt-2 border-t border-border">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">BIMCO Clause</span>
                    <span className="font-medium text-foreground">{calculationResults?.costAllocation?.bimcoClause}</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="maritime-card p-4">
              <h4 className="text-lg font-medium text-foreground mb-4">Compliance Status</h4>
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-muted-foreground">FuelEU Maritime</span>
                  <span className={`font-medium ${getComplianceColor(calculationResults?.fueleuCompliance?.complianceStatus)}`}>
                    {calculationResults?.fueleuCompliance?.complianceStatus === 'non-compliant' ? 'Non-Compliant' : 'Compliant'}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-muted-foreground">CII Rating Risk</span>
                  <span className={`font-medium ${getCIIRatingColor(calculationResults?.ciiRating?.projected)}`}>
                    {calculationResults?.ciiRating?.riskLevel === 'high' ? 'High Risk' : 'Low Risk'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Potential Penalty</span>
                  <span className="font-medium text-error">€{calculationResults?.fueleuCompliance?.penaltyRisk?.toLocaleString()}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      {activeTab === 'breakdown' && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Fuel Breakdown Pie Chart */}
            <div>
              <h3 className="text-lg font-medium text-foreground mb-4">Emissions by Fuel Type</h3>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={calculationResults?.fuelBreakdown}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ fuel, percentage }) => `${fuel} (${percentage}%)`}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="emissions"
                    >
                      {calculationResults?.fuelBreakdown?.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS?.[index % COLORS?.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Detailed Breakdown Table */}
            <div>
              <h3 className="text-lg font-medium text-foreground mb-4">Detailed Breakdown</h3>
              <div className="space-y-3">
                {calculationResults?.fuelBreakdown?.map((fuel, index) => (
                  <div key={fuel?.fuel} className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
                    <div className="flex items-center space-x-3">
                      <div 
                        className="w-4 h-4 rounded-full"
                        style={{ backgroundColor: COLORS?.[index % COLORS?.length] }}
                      />
                      <div>
                        <div className="font-medium text-foreground">{fuel?.fuel}</div>
                        <div className="text-sm text-muted-foreground">{fuel?.consumption} MT consumed</div>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="font-medium text-foreground">{fuel?.emissions} tCO₂e</div>
                      <div className="text-sm text-muted-foreground">{fuel?.percentage}%</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
      {activeTab === 'compliance' && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* FuelEU Maritime */}
            <div className="maritime-card p-4">
              <h3 className="text-lg font-medium text-foreground mb-4">FuelEU Maritime Compliance</h3>
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <span className="text-muted-foreground">GHG Intensity</span>
                  <span className="font-medium text-foreground">{calculationResults?.fueleuCompliance?.ghgIntensity} gCO₂e/MJ</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-muted-foreground">Target Intensity</span>
                  <span className="font-medium text-foreground">{calculationResults?.fueleuCompliance?.targetIntensity} gCO₂e/MJ</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-muted-foreground">Status</span>
                  <span className={`font-medium ${getComplianceColor(calculationResults?.fueleuCompliance?.complianceStatus)}`}>
                    {calculationResults?.fueleuCompliance?.complianceStatus === 'non-compliant' ? 'Non-Compliant' : 'Compliant'}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-muted-foreground">Penalty Risk</span>
                  <span className="font-medium text-error">€{calculationResults?.fueleuCompliance?.penaltyRisk?.toLocaleString()}</span>
                </div>
              </div>
            </div>

            {/* CII Rating */}
            <div className="maritime-card p-4">
              <h3 className="text-lg font-medium text-foreground mb-4">CII Rating Impact</h3>
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <span className="text-muted-foreground">Current Rating</span>
                  <span className={`font-medium ${getCIIRatingColor(calculationResults?.ciiRating?.current)}`}>
                    {calculationResults?.ciiRating?.current}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-muted-foreground">Projected Rating</span>
                  <span className={`font-medium ${getCIIRatingColor(calculationResults?.ciiRating?.projected)}`}>
                    {calculationResults?.ciiRating?.projected}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-muted-foreground">Risk Level</span>
                  <span className={`font-medium ${calculationResults?.ciiRating?.riskLevel === 'high' ? 'text-error' : 'text-success'}`}>
                    {calculationResults?.ciiRating?.riskLevel === 'high' ? 'High Risk' : 'Low Risk'}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-muted-foreground">Fleet Impact</span>
                  <span className={`font-medium ${calculationResults?.ciiRating?.impactOnFleet === 'negative' ? 'text-error' : 'text-success'}`}>
                    {calculationResults?.ciiRating?.impactOnFleet === 'negative' ? 'Negative' : 'Positive'}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      {activeTab === 'comparison' && (
        <div className="space-y-6">
          <div className="maritime-card p-4">
            <h3 className="text-lg font-medium text-foreground mb-4">Performance Comparison</h3>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="text-center p-4 bg-muted/30 rounded-lg">
                <div className="text-2xl font-bold text-foreground">{calculationResults?.totalEmissions?.toLocaleString()}</div>
                <div className="text-sm text-muted-foreground">This Voyage</div>
              </div>
              <div className="text-center p-4 bg-muted/30 rounded-lg">
                <div className="text-2xl font-bold text-foreground">{calculationResults?.comparisonData?.fleetAverage?.toLocaleString()}</div>
                <div className="text-sm text-muted-foreground">Fleet Average</div>
              </div>
              <div className="text-center p-4 bg-muted/30 rounded-lg">
                <div className="text-2xl font-bold text-foreground">{calculationResults?.comparisonData?.industryBenchmark?.toLocaleString()}</div>
                <div className="text-sm text-muted-foreground">Industry Benchmark</div>
              </div>
              <div className="text-center p-4 bg-muted/30 rounded-lg">
                <div className="text-2xl font-bold text-success">{calculationResults?.comparisonData?.bestInClass?.toLocaleString()}</div>
                <div className="text-sm text-muted-foreground">Best in Class</div>
              </div>
            </div>
            
            <div className="mt-6">
              <div className={`p-4 rounded-lg ${
                calculationResults?.comparisonData?.performance === 'below-average' ?'bg-warning/10 border border-warning/20' :'bg-success/10 border border-success/20'
              }`}>
                <div className="flex items-center space-x-2">
                  <Icon 
                    name={calculationResults?.comparisonData?.performance === 'below-average' ? 'TrendingDown' : 'TrendingUp'} 
                    size={20} 
                    className={calculationResults?.comparisonData?.performance === 'below-average' ? 'text-warning' : 'text-success'}
                  />
                  <span className="font-medium text-foreground">
                    Performance: {calculationResults?.comparisonData?.performance === 'below-average' ? 'Below Average' : 'Above Average'}
                  </span>
                </div>
                <p className="text-sm text-muted-foreground mt-2">
                  This voyage emits {((calculationResults?.totalEmissions / calculationResults?.comparisonData?.fleetAverage - 1) * 100)?.toFixed(1)}% 
                  {calculationResults?.totalEmissions > calculationResults?.comparisonData?.fleetAverage ? ' more' : ' less'} than fleet average
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmissionsResults;